import { useEffect, useState } from 'react';
import {
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  XCircle,
  Loader2,
  Copy,
  Info,
  Cloud,
  Server,
  Mail,
  HelpCircle,
} from 'lucide-react';
import { emailApi, type DnsRecommendationsResult, type DnsRecommendationsChannel, type DnsRecommendationRecord, type DnsRecommendationDkim, type DnsRecommendationsOverallStatus } from '../../lib/api';

interface Props {
  domain: string;
}

// DNS Wizard - kontekstowe rekomendacje konfiguracji dla operatora
// niezaznajomionego z protokołami SPF/DMARC. Pokazuje:
// 1. Wykryte kanały wysyłki (SMTP klienta + DKIM heurystyki)
// 2. Per rekord SPF/DMARC: currentValue vs newValue diff + przycisk Skopiuj
// 3. Status DKIM per selector + ostrzeżenia alignment
// 4. Globalne ostrzeżenia kontekstowe
//
// Auto-fetch przy mount - endpoint tani (parallel DNS + 2 query DB).

export function DnsRecommendations({ domain }: Props) {
  const [data, setData] = useState<DnsRecommendationsResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    emailApi
      .getDnsRecommendations(domain)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.error?.message || err?.message || 'Nie udało się pobrać rekomendacji');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [domain]);

  if (loading) {
    return (
      <div className="px-4 py-6 border-t border-border-subtle bg-surface-base flex items-center gap-2 text-xs text-text-muted">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Analizuję konfigurację domeny...
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 py-3 border-t border-border-subtle bg-surface-base text-xs text-danger">
        Błąd: {error}
      </div>
    );
  }

  if (!data) return null;

  const hasChannels = data.channels.length > 0;
  const hasWarnings = data.warnings.length > 0;
  const dkimWithWarnings = data.records.dkim.filter((d) => d.warning);
  // Sekcja "Zalecane zmiany" pokazujemy TYLKO gdy są faktyczne zmiany.
  // Banner u góry mówi "OK", a szczegóły rekordów są w "Surowy audit DNS".
  // Dwa puste kafle "Bez zmian" zaśmiecają widok.
  const showRecommendationsSection = data.overallStatus.changesRequired > 0;

  return (
    <div className="border-t border-border-subtle bg-surface-base">
      {/* Banner overall status - jednoznaczny sygnał czy konfiguracja OK */}
      <div className="px-4 pt-4">
        <OverallStatusBanner status={data.overallStatus} />
      </div>

      {/* Aktywny tryb wysyłki. */}
      {data.activeChannelDescription && (
        <div className="px-4 pt-3">
          <ActiveModeIndicator
            description={data.activeChannelDescription}
            mode={data.activeSendingMode}
          />
        </div>
      )}

      {/* Sekcja 1: Twoja konfiguracja - wykryte kanały wysyłki */}
      <div className="px-4 py-4">
        <SectionHeader title="Twoja konfiguracja wysyłki" subtitle="Wykryte kanały dla tej domeny" />
        {hasChannels ? (
          <div className="space-y-2">
            {data.channels.map((channel, i) => (
              <ChannelRow key={i} channel={channel} />
            ))}
          </div>
        ) : (
          <div className="text-xs text-text-muted py-2">
            Nie wykryto żadnych kanałów wysyłki dla tej domeny. Skonfiguruj konto SMTP, aby otrzymać rekomendacje.
          </div>
        )}
      </div>

      {/* Sekcja 2: Zalecane zmiany - tylko gdy są faktyczne zmiany */}
      {hasChannels && showRecommendationsSection && (
        <div className="px-4 py-4 border-t border-border-subtle">
          <SectionHeader
            title="Zalecane zmiany w DNS"
            subtitle={`${data.overallStatus.changesRequired} ${data.overallStatus.changesRequired === 1 ? 'rekord wymaga' : 'rekordy wymagają'} aktualizacji`}
          />
          <div className="space-y-3">
            {data.records.spf.action !== 'no_change' && (
              <RecordCard
                recordType="SPF"
                host={data.domain}
                dnsType="TXT"
                recommendation={data.records.spf}
              />
            )}
            {data.records.dmarc.action !== 'no_change' && (
              <RecordCard
                recordType="DMARC"
                host={`_dmarc.${data.domain}`}
                dnsType="TXT"
                recommendation={data.records.dmarc}
              />
            )}
          </div>
        </div>
      )}

      {/* Sekcja 3: Status DKIM (no_change z provider mapping + alignment warnings) */}
      {data.records.dkim.length > 0 && (
        <div className="px-4 py-4 border-t border-border-subtle">
          <SectionHeader
            title="Aktywne klucze DKIM"
            subtitle={`Wykryto ${data.records.dkim.length} ${pluralize(data.records.dkim.length, 'klucz', 'klucze', 'kluczy')} podpisujących maile`}
          />
          <div className="space-y-2">
            {data.records.dkim.map((dkim, i) => (
              <DkimRow key={i} dkim={dkim} />
            ))}
          </div>
          {dkimWithWarnings.length > 0 && (
            <div className="mt-3 text-xs text-text-muted leading-relaxed">
              Klucze oznaczone żółto wymagają uwagi przed zaostrzeniem polityki DMARC.
            </div>
          )}
        </div>
      )}

      {/* Sekcja 4: Globalne warnings */}
      {hasWarnings && (
        <div className="px-4 py-4 border-t border-border-subtle">
          <SectionHeader title="Uwagi" subtitle="Kontekstowe ostrzeżenia konfiguracji" />
          <div className="space-y-2">
            {data.warnings.map((warning, i) => (
              <div
                key={i}
                className="flex items-start gap-2 p-3 rounded-lg bg-warning/10 border border-warning/20 text-xs text-text-secondary leading-relaxed"
              >
                <AlertTriangle className="w-4 h-4 text-warning flex-shrink-0 mt-0.5" />
                <div>{warning}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SectionHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="mb-3">
      <div className="text-sm font-semibold text-text-primary">{title}</div>
      {subtitle && <div className="text-xs text-text-secondary mt-0.5">{subtitle}</div>}
    </div>
  );
}

// Banner z jednoznacznym statusem konfiguracji - widoczny od razu na górze
// żeby user wiedział "wszystko OK" / "trzeba naprawić" bez czytania tabel.
function OverallStatusBanner({ status }: { status: DnsRecommendationsOverallStatus }) {
  const config = {
    ok: {
      Icon: CheckCircle,
      bg: 'bg-success/10 border-success/30',
      iconColor: 'text-success',
      titleColor: 'text-success',
    },
    warnings: {
      Icon: AlertCircle,
      bg: 'bg-warning/10 border-warning/30',
      iconColor: 'text-warning',
      titleColor: 'text-warning',
    },
    critical: {
      Icon: XCircle,
      bg: 'bg-danger/10 border-danger/30',
      iconColor: 'text-danger',
      titleColor: 'text-danger',
    },
  } as const;
  const cfg = config[status.level];
  const Icon = cfg.Icon;
  return (
    <div className={`flex items-start gap-3 p-4 rounded-xl border ${cfg.bg}`}>
      <Icon className={`w-6 h-6 ${cfg.iconColor} flex-shrink-0 mt-0.5`} />
      <div className="min-w-0">
        <div className={`text-sm font-semibold ${cfg.titleColor}`}>{status.title}</div>
        <div className="text-xs text-text-secondary mt-1 leading-relaxed">{status.message}</div>
      </div>
    </div>
  );
}

// Badge statusu autoryzacji per kanał - jednoznaczny sygnał czy kanał jest
// poprawnie skonfigurowany (autoryzowany w SPF, zweryfikowany itp.).
// Specjalny case: authorized + isActive=false → "Skonfigurowany, nieaktywny".
function ChannelStatusBadge({ status, reason, isActive }: { status?: DnsRecommendationsChannel['authStatus']; reason?: string; isActive?: boolean }) {
  if (!status) return null;
  const isInactiveAuthorized = status === 'authorized' && isActive === false;
  const effectiveStatus = isInactiveAuthorized ? 'inactive' : status;
  const config = {
    authorized: { Icon: CheckCircle, color: 'text-success bg-success/10 border-success/30', label: 'Aktywny' },
    inactive: { Icon: Info, color: 'text-text-muted bg-surface-hover border-border-subtle', label: 'Skonfigurowany, nieaktywny' },
    missing_spf: { Icon: AlertTriangle, color: 'text-warning bg-warning/10 border-warning/30', label: 'Wymaga uwagi' },
    unknown: { Icon: HelpCircle, color: 'text-text-muted bg-surface-hover border-border-subtle', label: 'Wykryty' },
  } as const;
  const cfg = config[effectiveStatus];
  const Icon = cfg.Icon;
  return (
    <div
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${cfg.color}`}
      title={reason}
    >
      <Icon className="w-3 h-3" />
      {cfg.label}
    </div>
  );
}

// Wskaźnik aktywnego trybu wysyłki - pokazuje JAKI kanał jest faktycznie używany
// do wysyłki maili (vs. "skonfigurowany w DNS").
function ActiveModeIndicator({ description, mode }: { description: string; mode: 'smtp' | 'unknown' }) {
  const config = {
    smtp: { Icon: Server, color: 'text-info', label: 'SMTP własny aktywny' },
    unknown: { Icon: HelpCircle, color: 'text-text-muted', label: 'Tryb nieustawiony' },
  } as const;
  const cfg = config[mode];
  const Icon = cfg.Icon;
  return (
    <div className="flex items-center gap-2 p-2.5 rounded-lg bg-surface-hover/30 border border-border-subtle">
      <Icon className={`w-4 h-4 ${cfg.color} flex-shrink-0`} />
      <div className="text-xs">
        <span className="text-text-muted">Aktywny tryb wysyłki: </span>
        <span className="text-text-primary font-medium">{description}</span>
      </div>
    </div>
  );
}

function pluralize(n: number, one: string, few: string, many: string): string {
  if (n === 1) return one;
  const lastDigit = n % 10;
  const lastTwo = n % 100;
  if (lastTwo >= 12 && lastTwo <= 14) return many;
  if (lastDigit >= 2 && lastDigit <= 4) return few;
  return many;
}

function ChannelRow({ channel }: { channel: DnsRecommendationsChannel }) {
  // Wskazania źródła wykrycia kanału (info-only, nie ocena - tę robi badge statusu)
  const evidenceLines: Array<{ Icon: typeof Server; label: string }> = [];
  if (channel.host) {
    evidenceLines.push({ Icon: Server, label: `SMTP: ${channel.host}` });
  }
  if (channel.selectors && channel.selectors.length > 0) {
    evidenceLines.push({ Icon: Mail, label: `DKIM: ${channel.selectors.join(', ')}` });
  }

  // Kolor tła karty zależy od statusu - subtle, ale wzmocnia czytelność.
  // Specjalny case: authorized + isActive=false → neutralny bg (nie zielony),
  // bo to "skonfigurowany ale nieużywany" a nie "aktywny ok".
  const isInactiveAuthorized = channel.authStatus === 'authorized' && channel.isActive === false;
  const bgConfig = {
    authorized: 'bg-success/5 border-success/20',
    missing_spf: 'bg-warning/5 border-warning/20',
    unknown: 'bg-surface-hover/30 border-border-subtle',
  } as const;
  const bg = isInactiveAuthorized
    ? 'bg-surface-hover/30 border-border-subtle'
    : channel.authStatus ? bgConfig[channel.authStatus] : 'bg-surface-hover/30 border-border-subtle';

  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg border ${bg}`}>
      <div className="w-8 h-8 rounded-lg bg-surface-hover flex items-center justify-center flex-shrink-0">
        <Cloud className="w-4 h-4 text-text-secondary" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="text-sm font-medium text-text-primary">{channel.provider}</div>
          <ChannelStatusBadge status={channel.authStatus} reason={channel.authStatusReason} isActive={channel.isActive} />
        </div>
        {evidenceLines.length > 0 && (
          <div className="mt-1 space-y-0.5">
            {evidenceLines.map((line, i) => {
              const Icon = line.Icon;
              return (
                <div key={i} className="text-xs flex items-center gap-1.5 text-text-secondary">
                  <Icon className="w-3 h-3 flex-shrink-0 text-text-muted" />
                  <span className="truncate">{line.label}</span>
                </div>
              );
            })}
          </div>
        )}
        {channel.authStatusReason && (channel.authStatus !== 'authorized' || isInactiveAuthorized) && (
          <div className="text-xs text-text-secondary mt-1.5 leading-relaxed">
            {channel.authStatusReason}
          </div>
        )}
        {channel.requiresInSpf && channel.authStatus === 'missing_spf' && (
          <div className="text-xs text-text-muted mt-1">
            Brakuje w SPF: <code className="px-1 py-0.5 rounded bg-surface-hover text-text-secondary">{channel.requiresInSpf}</code>
          </div>
        )}
      </div>
    </div>
  );
}

function RecordCard({
  recordType,
  host,
  dnsType,
  recommendation,
}: {
  recordType: string;
  host: string;
  dnsType: 'TXT' | 'CNAME';
  recommendation: DnsRecommendationRecord;
}) {
  const actionConfig = {
    create: { label: 'Utwórz nowy rekord', color: 'text-warning', bg: 'bg-warning/10 border-warning/30' },
    edit: { label: 'Edytuj istniejący rekord', color: 'text-info', bg: 'bg-info/10 border-info/30' },
    no_change: { label: 'Bez zmian', color: 'text-success', bg: 'bg-success/10 border-success/30' },
  } as const;

  const cfg = actionConfig[recommendation.action];

  return (
    <div className={`rounded-xl border ${cfg.bg} p-4`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-text-primary">{recordType}</span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cfg.color} bg-surface-base/60`}>
              {cfg.label}
            </span>
          </div>
          <div className="text-xs text-text-secondary mt-1 leading-relaxed">{recommendation.reason}</div>
        </div>
      </div>

      {/* Lokalizacja rekordu */}
      <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-2 text-xs mb-3">
        <div className="text-text-muted">Typ:</div>
        <code className="bg-surface-base px-2 py-1 rounded border border-border-subtle text-text-primary w-fit">
          {dnsType}
        </code>
        <div className="text-text-muted">Nazwa / Host:</div>
        <CopyableField value={host} />
      </div>

      {/* Diff: current vs new */}
      {recommendation.action !== 'no_change' && (
        <>
          {recommendation.currentValue && (
            <div className="mb-2">
              <div className="text-xs text-text-muted mb-1">Aktualna wartość:</div>
              <code className="block text-xs text-text-secondary bg-surface-base p-2 rounded border border-border-subtle break-all">
                {recommendation.currentValue}
              </code>
            </div>
          )}
          {recommendation.newValue && (
            <div className="mb-2">
              <div className="text-xs text-text-muted mb-1">
                {recommendation.action === 'create' ? 'Wartość do wklejenia:' : 'Nowa wartość:'}
              </div>
              <ValueWithCopy value={recommendation.newValue} highlight={recommendation.diff?.added} />
            </div>
          )}
          {recommendation.warning && (
            <div className="mt-3 flex items-start gap-2 p-2 rounded bg-danger/10 border border-danger/20 text-xs text-text-secondary">
              <AlertTriangle className="w-3.5 h-3.5 text-danger flex-shrink-0 mt-0.5" />
              <div>{recommendation.warning}</div>
            </div>
          )}
          <ActionInstructions action={recommendation.action} dnsType={dnsType} />
        </>
      )}

      {recommendation.action === 'no_change' && recommendation.currentValue && (
        <div>
          <div className="text-xs text-text-muted mb-1">Aktualna wartość:</div>
          <code className="block text-xs text-text-secondary bg-surface-base p-2 rounded border border-border-subtle break-all">
            {recommendation.currentValue}
          </code>
        </div>
      )}
    </div>
  );
}

function CopyableField({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };
  return (
    <div className="flex items-center gap-1">
      <code className="bg-surface-base px-2 py-1 rounded border border-border-subtle text-text-primary break-all flex-1 min-w-0">
        {value}
      </code>
      <button
        onClick={copy}
        className="p-1.5 rounded hover:bg-surface-hover text-text-secondary flex-shrink-0"
        title="Skopiuj"
        aria-label="Skopiuj wartość"
      >
        {copied ? <CheckCircle className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

function ValueWithCopy({ value, highlight }: { value: string; highlight?: string[] }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  // Renderuj wartość z podświetleniem dodanych fragmentów (jeśli highlight podany)
  const renderValue = () => {
    if (!highlight || highlight.length === 0) return value;
    let result: (string | React.ReactElement)[] = [value];
    for (const fragment of highlight) {
      const next: (string | React.ReactElement)[] = [];
      for (const part of result) {
        if (typeof part !== 'string') {
          next.push(part);
          continue;
        }
        const idx = part.indexOf(fragment);
        if (idx === -1) {
          next.push(part);
          continue;
        }
        if (idx > 0) next.push(part.slice(0, idx));
        next.push(
          <mark key={`${fragment}-${next.length}`} className="bg-success/20 text-success font-semibold rounded px-0.5">
            {fragment}
          </mark>,
        );
        next.push(part.slice(idx + fragment.length));
      }
      result = next;
    }
    return <>{result}</>;
  };

  return (
    <div className="flex items-start gap-1">
      <code className="bg-surface-base px-2 py-1.5 rounded border border-border-subtle text-text-primary break-all flex-1 min-w-0 text-xs leading-relaxed">
        {renderValue()}
      </code>
      <button
        onClick={copy}
        className="p-1.5 rounded hover:bg-surface-hover text-text-secondary flex-shrink-0"
        title="Skopiuj nową wartość"
        aria-label="Skopiuj nową wartość"
      >
        {copied ? <CheckCircle className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

function ActionInstructions({ action, dnsType }: { action: 'create' | 'edit' | 'no_change'; dnsType: string }) {
  if (action === 'no_change') return null;

  const text = action === 'edit'
    ? `Wejdź do panelu DNS u dostawcy domeny. Znajdź istniejący rekord ${dnsType} pod nazwą podaną wyżej i podmień jego wartość na nową. Zachowaj wszystkie pozostałe pola.`
    : `Wejdź do panelu DNS u dostawcy domeny. Dodaj nowy rekord ${dnsType} pod nazwą podaną wyżej z wartością do wklejenia. Pozostałe pola (TTL, priorytet) zostaw domyślne.`;

  return (
    <div className="mt-3 flex items-start gap-2 p-3 rounded bg-surface-hover/40 border border-border-subtle text-xs text-text-secondary leading-relaxed">
      <Info className="w-3.5 h-3.5 text-info flex-shrink-0 mt-0.5" />
      <div>{text}</div>
    </div>
  );
}

function DkimRow({ dkim }: { dkim: DnsRecommendationDkim }) {
  const hasWarning = !!dkim.warning;
  // Spójność ikona vs tło: warning = AlertTriangle żółty na żółtym tle,
  // ok = CheckCircle zielony na zielonym/neutralnym tle. Bez konfliktu wizualnego.
  const Icon = hasWarning ? AlertTriangle : CheckCircle;
  const iconColor = hasWarning ? 'text-warning' : 'text-success';
  const bg = hasWarning ? 'bg-warning/5 border-warning/30' : 'bg-success/5 border-success/20';
  return (
    <div className={`p-3 rounded-lg border ${bg}`}>
      <div className="flex items-start gap-2">
        <Icon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${iconColor}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <code className="text-xs text-text-primary">{dkim.selector}._domainkey</code>
            <span className="text-xs text-text-muted">→</span>
            <span className="text-xs font-medium text-text-primary">
              {dkim.provider || 'Nieznany dostawca'}
            </span>
            <span className="text-xs text-text-muted">({dkim.recordType})</span>
            {hasWarning && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium text-warning bg-warning/10 border-warning/30">
                Wymaga uwagi
              </span>
            )}
          </div>
        </div>
      </div>
      {hasWarning && (
        <div className="mt-2 ml-6 text-xs text-text-secondary leading-relaxed">{dkim.warning}</div>
      )}
    </div>
  );
}
